import {createI18n} from 'vue-i18n'
import messages from '@intlify/unplugin-vue-i18n/messages'
import {LocaleType} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/enum/LocaleType'
import {Capacitor} from '@capacitor/core'
import {isElectron} from '@toolify/client/src/modules/electron/singletons/isElectron'
import {resolveLocaleType} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/functions/resolveLocaleType'
import {resolveLocaleTypeFromLanguageTag} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/functions/resolveLocaleTypeFromLanguageTag'
import {GUEST_LOCALE_STORAGE_KEY} from '@toolify/client/src/modules/authentication/constants/guestAppearanceStorageKeys'
import {getLocalePrefixFromPath} from '@toolify/client/src/modules/authentication/functions/getLocalePrefixFromPath'
import {getStoredGuestLocale} from '@toolify/client/src/modules/authentication/functions/getStoredGuestLocale'
import {clearVueI18nDevMeasuresPeriodically} from '@toolify/client/src/setup/functions/clearVueI18nDevMeasures'

const getLocaleFromLocationPath = (): LocaleType | null => {
  if(typeof window === 'undefined') {
    return null
  }
  const hashPath = window.location.hash?.startsWith('#')
    ? window.location.hash.slice(1)
    : ''
  const path = hashPath || window.location.pathname || ''
  return getLocalePrefixFromPath(path)
}

const getInitialLocale = (): LocaleType => {
  const pathLocale = getLocaleFromLocationPath()
  if(pathLocale) {
    return resolveLocaleType(pathLocale)
  }
  const storedLocale = getStoredGuestLocale()
  if(storedLocale) {
    return resolveLocaleType(storedLocale)
  }

  if(Capacitor.isNativePlatform() || isElectron()) {
    return resolveLocaleTypeFromLanguageTag(
      typeof navigator === 'undefined' ? undefined : navigator.language,
    )
  }

  return LocaleType.English
}

// Polish agrees a noun with the numeral in front of it - one week, two weeks, five of weeks - and
// every three-form Polish message in the translations is already written for those three forms. The
// two-form rule vue-i18n falls back to picks the last of them for everything past one, so without
// this "2 tygodnie" reads as "2 tygodni". Messages carrying fewer than three forms keep the default
// behaviour.
const polishPluralRule = (choice: number, choicesLength: number): number => {
  if(choicesLength < 3) {
    return choice === 1 ? 0 : 1
  }
  if(choice === 1) {
    return 0
  }
  const lastDigit = choice % 10
  const lastTwoDigits = choice % 100
  const isTeen = lastTwoDigits >= 12 && lastTwoDigits <= 14
  return lastDigit >= 2 && lastDigit <= 4 && !isTeen ? 1 : 2
}

// TODO: Refactor
export const i18n = createI18n({
  locale: getInitialLocale(),
  fallbackLocale: LocaleType.English,
  messages,
  globalInjection: true,
  legacy: false,
  pluralRules: {
    [LocaleType.Polish]: polishPluralRule,
  },
})

if(!import.meta.env.PROD) {
  clearVueI18nDevMeasuresPeriodically()
}

export const setLanguage = (locale: LocaleType) => {
  i18n.global.locale.value = locale
  localStorage.setItem(GUEST_LOCALE_STORAGE_KEY, locale)
  document.querySelector('html')?.setAttribute('lang', locale)
}
